import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBuildDetail, useDecideApproval } from '../../api/builds.js';
import type { Approval, BuildEvent } from '../../api/types.js';
import { Alert, Button, Card, Spinner } from '../../components/ui.js';
import { BuildStatusBadge } from '../../status.js';
import { describeEvent, describeToolInput, eventsToTranscript } from '../../events.js';
import { downloadTextFile } from '../../spec-markdown.js';

const ACTIVE = new Set(['queued', 'running', 'awaiting_approval']);

export function BuildConsolePage() {
  const { projectId, buildId } = useParams<{ projectId: string; buildId: string }>();
  const detail = useBuildDetail(projectId ?? '', buildId ?? '');

  if (!projectId || !buildId) return <Alert>Missing build reference.</Alert>;
  if (detail.isPending) return <Spinner label="Loading build…" />;
  if (detail.isError || !detail.data) return <Alert>Could not load this build.</Alert>;

  const { build, events, approvals } = detail.data;
  const pending = approvals.filter((a) => a.decision === 'pending');
  const live = ACTIVE.has(build.status);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to={`/console/projects/${projectId}`} className="text-sm text-primary underline">
          ← Back to project
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-title text-3xl tracking-wide">Build console</h1>
        <BuildStatusBadge status={build.status} />
        {build.autoApprove ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
            auto-approving
          </span>
        ) : null}
        {live ? (
          <span className="flex items-center gap-1.5 text-sm text-primary">
            <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
            Live
          </span>
        ) : null}
        <span className="ml-auto text-sm opacity-60">
          {build.inputTokens + build.outputTokens} tokens · {events.length} events
        </span>
        <Button
          variant="secondary"
          disabled={events.length === 0}
          onClick={() =>
            downloadTextFile(
              `build-${buildId}-transcript.txt`,
              `${build.instructions ?? ''}\n\n---\n\n${eventsToTranscript(events)}`,
            )
          }
        >
          Download transcript
        </Button>
      </div>

      {build.instructions ? (
        <details className="rounded-lg bg-white p-4 text-sm shadow-sm ring-1 ring-secondary/5">
          <summary className="cursor-pointer font-medium">
            Build instructions (what the agent was given)
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words opacity-80">
            {build.instructions}
          </pre>
        </details>
      ) : null}

      {pending.length > 0 ? (
        <div className="flex flex-col gap-3">
          <h2 className="font-title text-xl tracking-wide text-secondary">
            Awaiting your approval ({pending.length})
          </h2>
          {pending.map((approval) => (
            <ApprovalCard
              key={approval.id}
              approval={approval}
              projectId={projectId}
              buildId={buildId}
            />
          ))}
        </div>
      ) : live ? (
        <Alert kind="info">
          The agent is working. Approvals will appear here when it needs one.
        </Alert>
      ) : null}

      <Card>
        <h2 className="mb-3 font-title text-xl tracking-wide">Transcript</h2>
        {events.length === 0 ? (
          <p className="opacity-70">No events yet.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {events.map((event) => (
              <TranscriptRow key={event.id} event={event} />
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function ApprovalCard({
  approval,
  projectId,
  buildId,
}: {
  approval: Approval;
  projectId: string;
  buildId: string;
}) {
  const decide = useDecideApproval(projectId, buildId);
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');
  const command = describeToolInput(approval.toolName, approval.toolInput);

  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
      <p className="mb-2 text-sm font-medium">
        The agent wants to run <span className="font-mono">{approval.toolName}</span>:
      </p>
      <pre className="mb-3 overflow-x-auto rounded-md bg-mark-dark p-3 text-sm text-neutral">
        {command}
      </pre>

      {decide.isError ? (
        <div className="mb-3">
          <Alert>Could not record the decision. It may already be resolved.</Alert>
        </div>
      ) : null}

      {denying ? (
        <div className="flex flex-col gap-2">
          <textarea
            className="min-h-16 rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder="Tell the agent why, so it can choose another approach…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              variant="danger"
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate({
                  approvalId: approval.id,
                  result: 'deny',
                  ...(reason.trim() ? { denyMessage: reason.trim() } : {}),
                })
              }
            >
              {decide.isPending ? 'Denying…' : 'Confirm deny'}
            </Button>
            <Button variant="secondary" onClick={() => setDenying(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={decide.isPending}
            onClick={() => decide.mutate({ approvalId: approval.id, result: 'allow' })}
          >
            {decide.isPending ? 'Allowing…' : 'Allow'}
          </Button>
          <Button
            variant="secondary"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate({ approvalId: approval.id, result: 'allow', always: true })
            }
            title="Allow this and auto-approve the rest of this build"
          >
            Always allow
          </Button>
          <Button variant="secondary" onClick={() => setDenying(true)}>
            Deny…
          </Button>
        </div>
      )}
    </div>
  );
}

const KIND_STYLES: Record<string, string> = {
  message: 'border-l-primary',
  tool: 'border-l-amber-400',
  status: 'border-l-secondary/30',
  meta: 'border-l-secondary/10',
};

function TranscriptRow({ event }: { event: BuildEvent }) {
  const summary = describeEvent(event);
  return (
    <li className={`border-l-2 pl-3 ${KIND_STYLES[summary.kind] ?? KIND_STYLES.meta}`}>
      <div className="text-sm font-medium">{summary.title}</div>
      {summary.detail ? (
        <pre className="mt-0.5 whitespace-pre-wrap break-words text-sm opacity-80">
          {summary.detail}
        </pre>
      ) : null}
    </li>
  );
}
